import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type GroupingRange,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import BadgeList, {
  type BadgeListItem,
} from "@/v2-backoffice-client/components/badgeList"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { useAddOnCatalogQuery } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.catalog.query"
import LicenseFeatureGroupTableRowToolbar from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupTableRowToolbar"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

const USAGE_EMPTY = "Empty"
const USAGE_UNUSED = "Unused"
const USAGE_IN_USE = "In use"

// Bundle-size bands — grouping by them splits starter bundles from the
// kitchen-sink packages.
const GROUPING_RANGE_FEATURES: GroupingRange[] = [
  { label: "Empty", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 10 features", test: (v) => Number(v) <= 10 },
  { label: "11 – 50 features", test: (v) => Number(v) <= 50 },
  { label: "50+ features", test: () => true },
]

// The catalog-hygiene bucket, derived per row: an Empty package grants
// nothing wherever it is carried, an Unused one is carried by nothing (a
// delete candidate), and everything else is live vocabulary. Groupable and
// filterable so the cleanup pass is one click.
type FeatureGroupRow = FeatureGroupFragment & {
  UsageStatus: typeof USAGE_EMPTY | typeof USAGE_UNUSED | typeof USAGE_IN_USE
}

function getUsageStatus(group: FeatureGroupFragment) {
  if (group.FeatureCount === 0) return USAGE_EMPTY
  if (group.PlanCount === 0 && group.AddOnCount === 0) return USAGE_UNUSED
  return USAGE_IN_USE
}

/** "A, B, C" → ["A", "B", "C"]; a NULL aggregate → []. */
function splitNames(names: string | null | undefined) {
  return (names ?? "")
    .split(", ")
    .map((name) => name.trim())
    .filter(Boolean)
}

/**
 * The panels behind the names in "Included in". The row carries the
 * licenses and add-ons that give the package as two name lists; the ids
 * come from the two catalogs the Licensing page's tab triggers already
 * hold in the Apollo cache, so a name opens its panel at no extra read. A
 * name the catalog no longer knows still shows, just without a link.
 */
type IncludedInPanels = {
  planIdByName: ReadonlyMap<string, number>
  addOnIdByName: ReadonlyMap<string, number>
}

function createFeatureGroupColumns(panels: IncludedInPanels) {
  return createColumns<FeatureGroupRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Package",
        defaultSize: 220,
        to: ({ row }) => `?v=featureGroup:${row.original.Id}`,
      },
      Code: {
        id: "Code",
        label: "Code",
        defaultSize: 140,
        filter: { type: "text", placeholder: "Code..." },
        renderCell({ row }) {
          return (
            <Badge size="sm" variant="neutral">
              {row.original.Code}
            </Badge>
          )
        },
        exportValue({ row }) {
          return row.original.Code
        },
      },
      IncludedIn: {
        id: "IncludedIn",
        label: "Included in",
        description:
          "The licenses and add-ons that give this package. A customer gets the package through any one of them. Empty = nothing gives it yet.",
        defaultSize: 260,
        filter: { type: "text", placeholder: "License or add-on..." },
        getValue: (row) =>
          [row.PlanNames, row.AddOnNames].filter(Boolean).join(", "),
        renderCell({ row }) {
          const items: BadgeListItem[] = [
            ...splitNames(row.original.PlanNames).map((name) => {
              const planId = panels.planIdByName.get(name)
              return {
                key: `license-${name}`,
                label: name,
                to: planId === undefined ? undefined : `?v=plan:${planId}`,
                hint: "license",
                tooltip: "A license that gives this package",
              }
            }),
            ...splitNames(row.original.AddOnNames).map((name) => {
              const addOnId = panels.addOnIdByName.get(name)
              return {
                key: `add-on-${name}`,
                label: name,
                to: addOnId === undefined ? undefined : `?v=addOn:${addOnId}`,
                variant: "outline" as const,
                hint: "add-on",
                tooltip: "An add-on that gives this package",
              }
            }),
          ]
          return (
            <BadgeList
              items={items}
              overflowTitle="Included in"
              emptyLabel={
                <span className="text-muted-foreground">Nothing yet</span>
              }
            />
          )
        },
        exportValue({ row }) {
          return [row.original.PlanNames, row.original.AddOnNames]
            .filter(Boolean)
            .join(", ")
        },
      },
      Description: {
        id: "Description",
        label: "Description",
        defaultSize: 280,
        renderCell({ row }) {
          return (
            <span className="text-muted-foreground">
              {row.original.Description}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Description ?? ""
        },
      },
      UsageStatus: {
        id: "UsageStatus",
        label: "Usage",
        description:
          "Empty = bundles no features, grants nothing wherever carried. Unused = no license or add-on carries it (a delete candidate). Group on it for the catalog-hygiene pass.",
        defaultSize: 90,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any usage",
          options: [
            {
              label: USAGE_IN_USE,
              value: USAGE_IN_USE,
              dotVariant: "success-1",
            },
            { label: USAGE_UNUSED, value: USAGE_UNUSED, dotVariant: "warning" },
            {
              label: USAGE_EMPTY,
              value: USAGE_EMPTY,
              dotVariant: "destructive",
            },
          ],
        },
        renderCell({ row }) {
          const status = row.original.UsageStatus
          const variant =
            status === USAGE_IN_USE
              ? "success-1"
              : status === USAGE_UNUSED
                ? "warning"
                : "destructive"
          return <DotText variant={variant}>{status}</DotText>
        },
      },
      FeatureCount: {
        id: "FeatureCount",
        label: "Features",
        description:
          "How many features the package bundles today, picked and reached by rule. A package with none grants nothing — licenses and add-ons carrying it unlock nothing until features are added.",
        defaultSize: 80,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_FEATURES,
        renderCell({ row }) {
          const value = row.original.FeatureCount
          if (value === 0) {
            return <span className="text-destructive">Empty</span>
          }
          const ruleCount = row.original.RuleCount
          return (
            <span className="tabular-nums">
              {value}
              {ruleCount > 0 && (
                <span className="text-muted-foreground">
                  {" "}
                  · {ruleCount} rule{ruleCount === 1 ? "" : "s"}
                </span>
              )}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.FeatureCount
        },
      },
      PlanCount: {
        id: "PlanCount",
        label: "Licenses",
        description: "How many licenses include this package.",
        defaultSize: 70,
        filter: { type: "number-range" },
        renderCell({ row }) {
          return row.original.PlanCount || null
        },
        exportValue({ row }) {
          return row.original.PlanCount
        },
        // A zero the cell leaves blank is a blank, not a value to average.
        summaryValue({ row }) {
          return row.original.PlanCount || null
        },
      },
      AddOnCount: {
        id: "AddOnCount",
        label: "Add-ons",
        description: "How many live add-ons unlock this package.",
        defaultSize: 80,
        filter: { type: "number-range" },
        renderCell({ row }) {
          return row.original.AddOnCount || null
        },
        exportValue({ row }) {
          return row.original.AddOnCount
        },
        // A zero the cell leaves blank is a blank, not a value to average.
        summaryValue({ row }) {
          return row.original.AddOnCount || null
        },
      },
      UpdatedAt: {
        id: "UpdatedAt",
        label: "Updated",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.UpdatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      CreatedAt: {
        id: "CreatedAt",
        label: "Created",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.CreatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type LicenseFeatureGroupTableProps = Omit<
  TableProps<FeatureGroupRow>,
  "data" | "columns"
>

// The package catalog board (backed by common.FeatureGroups): the vocabulary
// licenses and add-ons grant from (D14 — the engine is built, this board is
// where the catalog gets filled). Owns the groups query (shared via the
// Apollo cache with the tab trigger's count); creation lives in the page
// header (LicenseFeatureGroupCreateButton, the customers-page convention),
// per-package edits in the row menu.
function LicenseFeatureGroupTable(props: LicenseFeatureGroupTableProps) {
  const { className, ...tableProps } = props

  const { data, previousData, loading, error, refetch } =
    useFeatureGroupsQuery()
  // previousData keeps rows rendered through post-save evictions.
  const featureGroups = (data ?? previousData)?.getFeatureGroups ?? []

  // The two catalogs behind the "Included in" links: both already read by
  // the Licensing page's tab triggers, so this is the cache, not a request.
  const { data: plansData } = useLicensePlansQuery()
  const { data: addOnsData } = useAddOnCatalogQuery()
  const panels = useMemo<IncludedInPanels>(
    () => ({
      planIdByName: new Map(
        (plansData?.getLicensePlans ?? []).map((plan) => [plan.Name, plan.Id]),
      ),
      addOnIdByName: new Map(
        (addOnsData?.getAddOnCatalog ?? []).map((addOn) => [
          addOn.Name,
          addOn.Id,
        ]),
      ),
    }),
    [plansData?.getLicensePlans, addOnsData?.getAddOnCatalog],
  )

  const rows = useMemo<FeatureGroupRow[]>(
    () =>
      featureGroups.map((group) => ({
        ...group,
        UsageStatus: getUsageStatus(group),
      })),
    [featureGroups],
  )

  const inUseCount = useMemo(
    () => rows.filter((row) => row.UsageStatus === USAGE_IN_USE).length,
    [rows],
  )
  const unusedCount = useMemo(
    () => rows.filter((row) => row.UsageStatus === USAGE_UNUSED).length,
    [rows],
  )

  const columns = useMemo(() => createFeatureGroupColumns(panels), [panels])

  return (
    <Table
      className={cn("license-feature-group-table", className)}
      tableId="license-feature-group-table"
      data={rows}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: the boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="UsageStatus"
          urlParam="packages-status"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={USAGE_IN_USE}
            variant="success"
            iconSrc="check-circle"
            count={inUseCount}
          >
            In use
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={USAGE_UNUSED}
            count={unusedCount}
          >
            Unused
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={USAGE_EMPTY}
            variant="destructive"
            iconSrc="exclamation"
            count={rows.length - inUseCount - unusedCount}
          >
            Empty
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by package name..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="packages"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<FeatureGroupRow> column="Name">
        {(featureGroup) => (
          <LicenseFeatureGroupTableRowToolbar featureGroup={featureGroup} />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseFeatureGroupTable
